import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { StatusBadge } from "../components/StatusBadge";
import { ChevronRight, Plus } from "lucide-react";

export default function Assessment() {
  const nav = useNavigate();
  const { effectiveRole } = useAuth();
  const canCreate = ["pmo", "it_admin"].includes(effectiveRole);
  const [ws, setWs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [users, setUsers] = useState([]);
  const [cfg, setCfg] = useState({ statuses: [], phases: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", title: "", description: "", owner_id: "", status: "Not Started", phase: "Not Started" });

  const load = () => api.get("/workstreams").then((r) => setWs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const createWs = async () => {
    if (!form.code || !form.title) return toast.error("Code and title are required");
    try {
      const { data } = await api.post("/workstreams", { ...form, owner_id: form.owner_id || null });
      toast.success("Workstream created");
      setShowForm(false);
      setForm({ code: "", title: "", description: "", owner_id: "", status: "Not Started", phase: "Not Started" });
      nav(`/workstream/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const counts = {
    total: ws.length,
    assess_plan: ws.filter((w) => ["Assessment", "Planning"].includes(w.phase)).length,
    mitigate: ws.filter((w) => ["Mitigation", "Stabilize"].includes(w.phase)).length,
    risk: ws.filter((w) => ["At Risk", "Critical"].includes(w.status)).length,
  };
  const shown = filter === "All" ? ws : ws.filter((w) => w.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold text-[#BA0C2F] uppercase tracking-wider">01 · Program assessment</div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733] mt-1">Assessment &amp; Mitigation Workspace</h1>
          <p className="text-[#5A687C] mt-1 max-w-3xl leading-relaxed">
            Current-state findings across infrastructure, clinical applications, service delivery, and digital engagement —
            each finding mapped to the mitigation action that resolves it.
          </p>
        </div>
        {canCreate && (
          <button data-testid="new-workstream-btn" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 py-2.5 font-semibold shrink-0">
            <Plus className="h-4 w-4" /> New workstream
          </button>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 w-full max-w-lg mt-16 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="new-workstream-modal">
            <h2 className="text-lg font-bold text-[#1F2733] mb-4">Create a new workstream</h2>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="ws-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code (e.g. WS-10)" className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <select data-testid="ws-owner-select" value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm">
                <option value="">Owner (optional)</option>
                {users.filter((u) => ["it_executive", "pmo"].includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input data-testid="ws-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="col-span-2 px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="col-span-2 px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <select data-testid="ws-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm">
                {(cfg.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="ws-phase-select" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm">
                {(cfg.phases || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowForm(false)} className="rounded-full px-4 py-2 text-sm font-semibold text-[#5A687C] hover:bg-[#F4F6FA]">Cancel</button>
              <button data-testid="create-workstream-btn" onClick={createWs} className="rounded-full px-5 py-2 text-sm font-semibold bg-[#BA0C2F] text-white hover:bg-[#8A0822]">Create workstream</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Workstreams in program", counts.total, "#BA0C2F"],
          ["In assessment or planning", counts.assess_plan, "#BA0C2F"],
          ["In mitigation / build", counts.mitigate, "#2E8B57"],
          ["At risk or critical", counts.risk, "#BA0C2F"],
        ].map(([label, val, color]) => (
          <div key={label} className="bg-white rounded-[18px] border border-[#DCE2EA] p-5">
            <div className="text-4xl font-extrabold" style={{ color }}>{val}</div>
            <div className="text-xs text-[#5A687C] mt-1 font-medium">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 bg-[#F4F6FA] p-1.5 rounded-full border border-[#DCE2EA] w-fit">
        {["All", "On Track", "At Risk", "Critical", "Not Started"].map((f) => (
          <button key={f} data-testid={`assessment-filter-${f.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm transition-all ${filter === f ? "bg-[#BA0C2F] text-white font-semibold" : "text-[#5A687C] hover:bg-white/70 font-medium"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {shown.map((w) => (
          <button key={w.id} data-testid={`workstream-card-${w.code.toLowerCase()}`} onClick={() => nav(`/workstream/${w.id}`)}
            className="text-left bg-white rounded-[18px] border border-[#DCE2EA] p-6 shadow-[0_2px_10px_rgba(31,39,51,0.04)] hover:shadow-[0_12px_32px_rgba(56,96,190,0.12)] hover:-translate-y-0.5 transition-all group">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-white bg-[#BA0C2F] rounded-full px-2.5 py-1">{w.code}</span>
                <h3 className="text-lg font-bold text-[#1F2733] mt-2">{w.title}</h3>
              </div>
              <StatusBadge status={w.status} />
            </div>
            <p className="text-sm text-[#5A687C] mt-2 line-clamp-2">{w.description}</p>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-[#5A687C]">Phase · <span className="font-semibold text-[#1F2733]">{w.phase}</span></div>
              <div className="text-xs text-[#5A687C]">{w.health.task_done}/{w.health.task_total} tasks · {w.health.percent_complete}%</div>
            </div>
            <div className="h-1.5 bg-[#F1F3F6] rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-[#BA0C2F] rounded-full" style={{ width: `${w.health.percent_complete}%` }} />
            </div>
            <div className="mt-3 text-sm font-semibold text-[#BA0C2F] flex items-center gap-1 group-hover:gap-2 transition-all">
              Open workstream <ChevronRight className="h-4 w-4" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
